import { ColumnVisibilityConfig } from '../hooks/useColumnVisibility';
import { ColumnOption } from '../components/LimitItems';

export interface TableColumnDef<
    Id extends string,
    SortField extends string = string
> extends ColumnVisibilityConfig<Id> {
    label: string;
    /** Grid track size for this column on desktop. */
    size: string;
    /**
     * Backend field this column sorts by. Present only where the list endpoint supports it, which
     * is what makes the header clickable — a column without one renders as plain text.
     */
    sortField?: SortField;
    /** Header + cell content are centered in the track (icon and badge columns). */
    centered?: boolean;
}

interface GridTemplateOptions {
    /** Fixed tracks rendered before the toggleable columns (e.g. a bulk-select checkbox). */
    leadingTracks?: string[];
    /** Fixed tracks rendered after the toggleable columns (e.g. a trailing status icon). */
    trailingTracks?: string[];
    /** When true (mobile), collapse to a single stacked column. */
    collapsed?: boolean;
}

/**
 * Build a `grid-template-columns` string from the currently-visible columns so a
 * table's header and rows stay aligned. Shared by every customizable list.
 */
export const buildGridTemplate = <Id extends string>(
    columns: TableColumnDef<Id>[],
    isVisible: (id: Id) => boolean,
    { leadingTracks = [], trailingTracks = [], collapsed = false }: GridTemplateOptions = {}
): string => {
    if (collapsed) return '1fr';
    const tracks = columns.filter((column) => isVisible(column.id)).map((column) => column.size);
    return [...leadingTracks, ...tracks, ...trailingTracks].join(' ');
};

/** Build the `LimitItems` column-control options from a column config + visibility. */
export const buildColumnOptions = <Id extends string>(
    columns: TableColumnDef<Id>[],
    isVisible: (id: Id) => boolean
): ColumnOption[] =>
    columns.map((column) => ({
        id: column.id,
        label: column.label,
        isVisible: isVisible(column.id),
        disabled: column.alwaysVisible
    }));
